'use client'

import { useRef, useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X, Play, Pause, RotateCcw, RotateCw, CirclePlay } from 'lucide-react'
import { VideoPlayer, VideoPlayerRef } from './VideoPlayer'

// Skip amount in seconds
const SKIP_SECONDS = 10

export type VideoOverlayProps = {
  isOpen: boolean
  onClose: () => void
  playbackId: string
  title?: string
  aspectRatio?: 'portrait' | 'landscape' | 'square'
  autoPlay?: boolean
  /**
   * When true, the overlay opens without auto-playing — the user
   * sees the poster behind a dark scrim with a centered play button
   * and has to tap it to start. Use this when the overlay is opened
   * without a recent user gesture on the page (e.g. on first load
   * via `#video-<id>` from an email link), since browsers block
   * autoplay without a gesture and we'd rather show a clear
   * affordance than a frozen first frame.
   */
  requireTapToPlay?: boolean
}

/**
 * VideoOverlay displays a full-screen modal with video player.
 * Includes custom controls that appear on tap and auto-hide.
 */
export function VideoOverlay({
  isOpen,
  onClose,
  playbackId,
  title,
  aspectRatio = 'portrait',
  autoPlay = true,
  requireTapToPlay = false,
}: VideoOverlayProps) {
  const playerRef = useRef<VideoPlayerRef>(null)
  const progressBarRef = useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [showControls, setShowControls] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // CRITICAL: Track whether we've attempted autoplay to prevent multiple attempts
  const hasTriedAutoplayRef = useRef(false)
  // True after the user taps the dark-scrim play button. Hides the
  // scrim and switches the player into normal autoplay behavior. The
  // tap counts as a user gesture so play() won't be rejected.
  const [hasTappedToPlay, setHasTappedToPlay] = useState(false)
  const showTapToPlayScrim = requireTapToPlay && !hasTappedToPlay

  // Format time as M:SS
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }, [])

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      playerRef.current?.pause()
      setIsPlaying(false)
      setCurrentTime(0)
      setShowControls(false)
      setIsReady(false)
      setHasTappedToPlay(false)
      hasTriedAutoplayRef.current = false // Reset for next open
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
    }
  }, [isOpen])

  // Setup direct event listeners on the MuxPlayer element for reliable autoplay
  // This is CRITICAL - React props like onLoadedMetadata fire before the player is truly ready
  useEffect(() => {
    if (!isOpen || !autoPlay) return

    const player = playerRef.current?.getElement()
    if (!player) return

    // Handler for canplay event - this fires when player is TRULY ready to play
    const handleCanPlay = () => {
      setIsReady(true)
      // In tap-to-play mode we still want to mark the player as ready
      // (so the poster + scrim can render), but hold off the actual
      // play() call until the user taps. The scrim's onClick calls
      // player.play() directly, carrying its own user gesture.
      if (showTapToPlayScrim) return
      if (!hasTriedAutoplayRef.current) {
        hasTriedAutoplayRef.current = true
        player.play().catch(() => {
          // Autoplay may be blocked by browser - that's okay
        })
      }
    }

    // Handler for play event
    const handlePlay = () => {
      setIsPlaying(true)
      setIsReady(true)
    }

    // Handler for pause event
    const handlePause = () => {
      setIsPlaying(false)
    }

    // Handler for loadedmetadata - just for duration, NOT for triggering play
    const handleLoadedMetadata = () => {
      if (player.duration) {
        setDuration(player.duration)
      }
    }

    // Handler for timeupdate
    const handleTimeUpdate = () => {
      setCurrentTime(player.currentTime)
    }

    // Attach event listeners directly to the MuxPlayer element
    player.addEventListener('canplay', handleCanPlay)
    player.addEventListener('play', handlePlay)
    player.addEventListener('pause', handlePause)
    player.addEventListener('loadedmetadata', handleLoadedMetadata)
    player.addEventListener('timeupdate', handleTimeUpdate)

    // Get duration if already loaded
    if (player.duration) {
      setDuration(player.duration)
    }

    return () => {
      player.removeEventListener('canplay', handleCanPlay)
      player.removeEventListener('play', handlePlay)
      player.removeEventListener('pause', handlePause)
      player.removeEventListener('loadedmetadata', handleLoadedMetadata)
      player.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [isOpen, autoPlay, showTapToPlayScrim])

  // Fallback: Poll for readyState as backup in case canplay event doesn't fire
  // This is the proven pattern from VideoPlayerDialog that handles edge cases
  useEffect(() => {
    if (!isOpen || !autoPlay) return

    // Show controls briefly so user knows they exist
    setShowControls(true)
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false)
    }, 2000)

    let attempts = 0
    const maxAttempts = 50 // 5 seconds total (50 * 100ms)
    let intervalId: NodeJS.Timeout | null = null

    const tryPlay = () => {
      const player = playerRef.current?.getElement()
      if (!player) return false

      // Skip if we've already triggered autoplay via canplay event
      if (hasTriedAutoplayRef.current) return true

      // Check if player is ready enough to play (readyState >= 3 means HAVE_FUTURE_DATA)
      if (player.readyState >= 3) {
        setIsReady(true)
        // In tap-to-play mode, stop polling once ready but don't
        // play yet — the scrim's onClick will trigger play() with a
        // user gesture.
        if (showTapToPlayScrim) return true
        hasTriedAutoplayRef.current = true
        player.play().catch(() => {
          // Autoplay may be blocked by browser - that's okay
        })
        return true
      }
      return false
    }

    // Delay the first attempt to let MuxPlayer component fully mount
    // This is CRITICAL for first video - MuxPlayer needs time to initialize
    const initialDelayId = setTimeout(() => {
      if (!tryPlay()) {
        // If not ready, poll every 100ms until ready or max attempts reached
        intervalId = setInterval(() => {
          attempts++
          if (tryPlay() || attempts >= maxAttempts) {
            if (intervalId) clearInterval(intervalId)
            // If max attempts reached, show player anyway (might need manual play)
            if (attempts >= maxAttempts && !hasTriedAutoplayRef.current) {
              setIsReady(true)
            }
          }
        }, 100)
      }
    }, 200) // 200ms initial delay for MuxPlayer to mount

    return () => {
      clearTimeout(initialDelayId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [isOpen, autoPlay, showTapToPlayScrim])

  // Show controls when tapping video
  const handleVideoClick = () => {
    if (showControls) {
      setShowControls(false)
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
    } else {
      setShowControls(true)
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false)
      }, 3000)
    }
  }

  const resetControlsTimer = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false)
    }, 3000)
  }

  const handleTapToPlay = () => {
    setHasTappedToPlay(true)
    const player = playerRef.current?.getElement()
    if (!player) return
    if (!hasTriedAutoplayRef.current) {
      hasTriedAutoplayRef.current = true
      // The tap is a fresh user gesture, so unmuted play() is allowed.
      player.play().catch(() => {
        // If something else still rejects (rare), the player just
        // sits paused — the user can use the standard play control.
      })
    }
  }

  const handlePlayPause = () => {
    const player = playerRef.current?.getElement()
    if (!player) return
    if (player.paused) {
      player.play()
    } else {
      player.pause()
    }
    resetControlsTimer()
  }

  const handleSkipBack = () => {
    const player = playerRef.current?.getElement()
    if (!player) return
    player.currentTime = Math.max(0, player.currentTime - SKIP_SECONDS)
    resetControlsTimer()
  }

  const handleSkipForward = () => {
    const player = playerRef.current?.getElement()
    if (!player) return
    player.currentTime = Math.min(duration, player.currentTime + SKIP_SECONDS)
    resetControlsTimer()
  }

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const player = playerRef.current?.getElement()
    if (!player || !progressBarRef.current) return
    const rect = progressBarRef.current.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = clickX / rect.width
    player.currentTime = percentage * duration
    resetControlsTimer()
  }

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Don't render on server or when closed
  if (!isOpen || typeof document === 'undefined') return null

  // Use portal to render outside any stacking context
  return createPortal(
    <div
      className="fixed inset-0 bg-black flex flex-col"
      style={{
        zIndex: 9999,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 flex-shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)', paddingBottom: '8px' }}
      >
        <h1 className="flex-1 text-xl font-bold text-white truncate">
          {title || 'Video'}
        </h1>
        <button
          onClick={onClose}
          className="w-11 h-11 flex items-center justify-center cursor-pointer text-white"
          aria-label="Close"
        >
          <X size={28} strokeWidth={2} />
        </button>
      </div>

      {/* Video container */}
      <div className="flex-1 flex items-center justify-center px-4 pt-0 pb-4 overflow-hidden">
        <div
          className="relative cursor-pointer"
          style={{
            opacity: isReady ? 1 : 0,
            visibility: isReady ? 'visible' : 'hidden',
            transition: 'opacity 0.15s ease-in',
            borderRadius: '12px',
            overflow: 'hidden',
            aspectRatio: '400 / 720',
            height: '100%',
            width: 'auto',
            maxWidth: '400px',
            maxHeight: '100%',
          }}
          onClick={handleVideoClick}
        >
          <VideoPlayer
            ref={playerRef}
            playbackId={playbackId}
            aspectRatio={aspectRatio}
            autoPlay={autoPlay}
          />

          {/* Tap-to-play scrim — shown over the poster when the
             overlay was opened without a user gesture (e.g. via a
             #video-<id> hash from an email). Tapping it carries a
             fresh user gesture into the play() call. */}
          {showTapToPlayScrim && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleTapToPlay()
              }}
              aria-label="Play video"
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.69)',
                border: 'none',
                padding: 0,
                borderRadius: 'inherit',
                zIndex: 5,
              }}
            >
              <CirclePlay size={88} strokeWidth={1} className="text-white drop-shadow-lg" />
            </button>
          )}

          {/* Controls overlay */}
          {showControls && (
            <div
              className="absolute bottom-0 left-0 right-0 px-4 py-3"
              style={{ backgroundColor: '#000000', borderRadius: '0 0 12px 12px' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Progress bar */}
              <div
                ref={progressBarRef}
                className="h-2 rounded-full cursor-pointer"
                style={{ backgroundColor: 'rgba(229, 226, 222, 0.3)' }}
                onClick={handleProgressBarClick}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    backgroundColor: '#E5E2DE',
                    width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%'
                  }}
                />
              </div>

              {/* Time and controls */}
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm w-12" style={{ color: 'rgba(229, 226, 222, 0.7)' }}>
                  {formatTime(currentTime)}
                </span>

                <div className="flex items-center gap-4">
                  <button
                    onClick={handleSkipBack}
                    className="w-11 h-11 flex items-center justify-center cursor-pointer"
                    style={{ color: '#E5E2DE' }}
                    aria-label="Skip back 10 seconds"
                  >
                    <RotateCcw size={24} />
                  </button>

                  <button
                    onClick={handlePlayPause}
                    className="w-11 h-11 flex items-center justify-center cursor-pointer"
                    style={{ color: '#E5E2DE' }}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? <Pause size={28} /> : <Play size={28} />}
                  </button>

                  <button
                    onClick={handleSkipForward}
                    className="w-11 h-11 flex items-center justify-center cursor-pointer"
                    style={{ color: '#E5E2DE' }}
                    aria-label="Skip forward 10 seconds"
                  >
                    <RotateCw size={24} />
                  </button>
                </div>

                <span className="text-sm w-12 text-right" style={{ color: 'rgba(229, 226, 222, 0.7)' }}>
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
